/**
 * Fjórir kraftar — a 9×9 hunt for four upgrades, then home to the centre.
 *
 * Positions are row-major with row 0 at the top of the screen: `row * 9 + col`.
 * Left is west (vinstri), right is east (hægri), up is toward row 0.
 * The protocol id stays `find_four`.
 */

/** Classroom name shown in the builder, rules, and settings. */
export const FIND_FOUR_TITLE = 'Fjórir kraftar';

/** Standings copy: 1 kraftur, otherwise N kraftar. */
export function kraftarLabel(count: number): string {
  const n = Math.max(0, Math.trunc(count));
  return n === 1 ? '1 kraftur' : `${String(n)} kraftar`;
}

export const FIND_FOUR_SIZE = 9;
export const FIND_FOUR_TILES = FIND_FOUR_SIZE * FIND_FOUR_SIZE;
export const FIND_FOUR_RED_COUNT = 25;

export const FIND_FOUR_CENTER_COL = 4;
export const FIND_FOUR_CENTER_ROW = 4;

export const powerupIdSchema = ['shoes', 'hammer', 'bow', 'clothes'] as const;
export type PowerupId = (typeof powerupIdSchema)[number];

export const POWERUP_IDS: readonly PowerupId[] = powerupIdSchema;

export type FindFourAxis = 'left' | 'right' | 'up' | 'down';

export interface FindFourCell {
  col: number;
  row: number;
}

export interface FindFourPowerup {
  position: number;
  id: PowerupId;
}

export interface FindFourBoard {
  redPositions: number[];
  powerups: FindFourPowerup[];
}

export interface FindFourMoveInput {
  right: number;
  left: number;
  up: number;
  down: number;
}

export function findFourPosition(col: number, row: number): number {
  return row * FIND_FOUR_SIZE + col;
}

export const FIND_FOUR_CENTER = findFourPosition(FIND_FOUR_CENTER_COL, FIND_FOUR_CENTER_ROW);

export const FIND_FOUR_CORNERS: readonly number[] = [
  findFourPosition(0, 0),
  findFourPosition(FIND_FOUR_SIZE - 1, 0),
  findFourPosition(0, FIND_FOUR_SIZE - 1),
  findFourPosition(FIND_FOUR_SIZE - 1, FIND_FOUR_SIZE - 1),
];

export function findFourCell(position: number): FindFourCell {
  return { col: position % FIND_FOUR_SIZE, row: Math.floor(position / FIND_FOUR_SIZE) };
}

export function isFindFourPosition(position: number): boolean {
  return Number.isInteger(position) && position >= 0 && position < FIND_FOUR_TILES;
}

/**
 * Credit 0..1 → tiles moved. Shared by Fjórir kraftar and Snákar og stigar.
 *
 * Inclusive upper bounds so `movementFor` in scoring reads the same rungs as
 * `tilesForCredit`: 0–15% → 1, 16–30% → 2, 31–45% → 3, 46–60% → 4,
 * 61–75% → 5, 76–88% → 6, 89–99% → 7, 100% → 8.
 */
export const CREDIT_MOVEMENT_BANDS: { maxCredit: number; tiles: number }[] = [
  { maxCredit: 0.15, tiles: 1 },
  { maxCredit: 0.3, tiles: 2 },
  { maxCredit: 0.45, tiles: 3 },
  { maxCredit: 0.6, tiles: 4 },
  { maxCredit: 0.75, tiles: 5 },
  { maxCredit: 0.88, tiles: 6 },
  { maxCredit: 0.99, tiles: 7 },
  { maxCredit: 1, tiles: 8 },
];

/**
 * Credit 0..1 → tiles, using the shared percentage rungs.
 *
 * Percent is rounded so 15.4% stays on 1 tile and 15.5% steps up to 2.
 * A band at 1.0 is only an exact clean sweep.
 */
export function tilesForCredit(credit: number): number {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(credit) ? credit : 0));
  const percent = Math.round(clamped * 100);
  if (percent >= 100) return 8;
  if (percent >= 89) return 7;
  if (percent >= 76) return 6;
  if (percent >= 61) return 5;
  if (percent >= 46) return 4;
  if (percent >= 31) return 3;
  if (percent >= 16) return 2;
  return 1;
}

/**
 * Fjórir kraftar movement budget: shared rungs, plus shoes (+2) once collected
 * (from a later movement round, not the turn they are picked up).
 */
export function findFourBudget(credit: number, hasShoes: boolean): number {
  return tilesForCredit(credit) + (hasShoes ? 2 : 0);
}

export function maxStepsOnAxis(position: number, axis: FindFourAxis): number {
  const { col, row } = findFourCell(position);
  switch (axis) {
    case 'left':
      return col;
    case 'right':
      return FIND_FOUR_SIZE - 1 - col;
    case 'up':
      return row;
    case 'down':
      return FIND_FOUR_SIZE - 1 - row;
  }
}

export function normalizeFindFourMove(input: FindFourMoveInput): FindFourMoveInput {
  let right = Math.max(0, Math.trunc(input.right));
  let left = Math.max(0, Math.trunc(input.left));
  let up = Math.max(0, Math.trunc(input.up));
  let down = Math.max(0, Math.trunc(input.down));
  if (right > 0) left = 0;
  else if (left > 0) right = 0;
  if (down > 0) up = 0;
  else if (up > 0) down = 0;
  return { right, left, up, down };
}

export function findFourSpend(move: FindFourMoveInput): number {
  const n = normalizeFindFourMove(move);
  return n.right + n.left + n.up + n.down;
}

function slot(value: number): number {
  return Math.max(0, Math.trunc(Number.isFinite(value) ? value : 0));
}

/**
 * Stay put, one direction, or one horizontal plus one vertical. Left+right or
 * up+down together is not allowed.
 */
export function isLegalFindFourSpend(input: FindFourMoveInput): boolean {
  const right = slot(input.right);
  const left = slot(input.left);
  const up = slot(input.up);
  const down = slot(input.down);
  return !(right > 0 && left > 0) && !(up > 0 && down > 0);
}

export function activeFindFourAxis(input: FindFourMoveInput): FindFourAxis | null {
  const n = {
    right: Math.max(0, Math.trunc(input.right)),
    left: Math.max(0, Math.trunc(input.left)),
    up: Math.max(0, Math.trunc(input.up)),
    down: Math.max(0, Math.trunc(input.down)),
  };
  if (n.right > 0) return 'right';
  if (n.left > 0) return 'left';
  if (n.up > 0) return 'up';
  if (n.down > 0) return 'down';
  return null;
}

const OPPOSITE_AXIS: Record<FindFourAxis, FindFourAxis> = {
  left: 'right',
  right: 'left',
  up: 'down',
  down: 'up',
};

/**
 * Caps each direction at the edge, then the remaining budget. Horizontal
 * steps are kept first when the total would overflow.
 */
export function clampFindFourMove(
  from: number,
  budget: number,
  input: FindFourMoveInput,
): FindFourMoveInput {
  const empty: FindFourMoveInput = { right: 0, left: 0, up: 0, down: 0 };
  if (!isLegalFindFourSpend(input)) return empty;
  const n = normalizeFindFourMove(input);
  const take = (axis: FindFourAxis, remaining: number): number =>
    Math.min(n[axis], remaining, maxStepsOnAxis(from, axis));

  let remaining = Math.max(0, Math.trunc(budget));
  const right = take('right', remaining);
  remaining -= right;
  const left = take('left', remaining);
  remaining -= left;
  const up = take('up', remaining);
  remaining -= up;
  const down = take('down', remaining);
  return { right, left, up, down };
}

export function axisCap(
  from: number,
  axis: FindFourAxis,
  budget: number,
  draft: FindFourMoveInput,
): number {
  if (slot(draft[OPPOSITE_AXIS[axis]]) > 0) return 0;
  const spentElsewhere = findFourSpend({ ...draft, [axis]: 0 });
  const remaining = Math.max(0, Math.trunc(budget) - spentElsewhere);
  return Math.min(remaining, maxStepsOnAxis(from, axis));
}

export const POWERUP_LABELS: Record<PowerupId, string> = {
  shoes: 'Skór',
  hammer: 'Hamar',
  bow: 'Bogi',
  clothes: 'Hlífðarföt',
};

/** Dative forms for sentences like "Grænt lið náði hamri". */
export const POWERUP_LABELS_DATIVE: Record<PowerupId, string> = {
  shoes: 'skóm',
  hammer: 'hamri',
  bow: 'boga',
  clothes: 'hlífðarfötum',
};

export interface BoardShot {
  teamId: string;
  from: number;
  axis: FindFourAxis;
  line: number[];
}

export function parsePowerups(raw: unknown): PowerupId[] {
  if (!Array.isArray(raw)) return [];
  const allowed = new Set<string>(POWERUP_IDS);
  const out: PowerupId[] = [];
  for (const item of raw) {
    if (typeof item !== 'string' || !allowed.has(item)) continue;
    const id = item as PowerupId;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Walks horizontal first, then vertical. Empty when the team stays put.
 */
export function findFourPath(from: number, input: FindFourMoveInput): number[] {
  const move = normalizeFindFourMove(input);
  const { col, row } = findFourCell(from);
  const path: number[] = [];

  const dCol = move.right - move.left;
  const dRow = move.down - move.up;

  let c = col;
  const colStep = Math.sign(dCol);
  for (let i = 0; i < Math.abs(dCol); i++) {
    c += colStep;
    path.push(findFourPosition(c, row));
  }

  let r = row;
  const rowStep = Math.sign(dRow);
  for (let i = 0; i < Math.abs(dRow); i++) {
    r += rowStep;
    path.push(findFourPosition(c, r));
  }

  return path;
}

/** Cell-by-cell walk, horizontal first, then vertical. Empty when `from === to`. */
export function findFourWalk(from: number, to: number): number[] {
  if (from === to) return [];
  const start = findFourCell(from);
  const end = findFourCell(to);
  return findFourPath(from, {
    right: Math.max(0, end.col - start.col),
    left: Math.max(0, start.col - end.col),
    down: Math.max(0, end.row - start.row),
    up: Math.max(0, start.row - end.row),
  });
}

export function neighbours3x3(position: number): number[] {
  const { col, row } = findFourCell(position);
  const out: number[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      const nc = col + dc;
      const nr = row + dr;
      if (nc < 0 || nc >= FIND_FOUR_SIZE || nr < 0 || nr >= FIND_FOUR_SIZE) continue;
      out.push(findFourPosition(nc, nr));
    }
  }
  return out;
}

export function bowLine(from: number, axis: FindFourAxis): number[] {
  const { col, row } = findFourCell(from);
  const line: number[] = [];
  switch (axis) {
    case 'left':
      for (let c = col - 1; c >= 0; c--) line.push(findFourPosition(c, row));
      break;
    case 'right':
      for (let c = col + 1; c < FIND_FOUR_SIZE; c++) line.push(findFourPosition(c, row));
      break;
    case 'up':
      for (let r = row - 1; r >= 0; r--) line.push(findFourPosition(col, r));
      break;
    case 'down':
      for (let r = row + 1; r < FIND_FOUR_SIZE; r++) line.push(findFourPosition(col, r));
      break;
  }
  return line;
}

export function hasAllPowerups(owned: readonly PowerupId[]): boolean {
  return POWERUP_IDS.every((id) => owned.includes(id));
}

export function collectPowerupAt(
  owned: readonly PowerupId[],
  powerups: readonly FindFourPowerup[],
  position: number,
): PowerupId[] {
  const found = powerups.find((item) => item.position === position);
  if (found === undefined || owned.includes(found.id)) return [...owned];
  return [...owned, found.id];
}

function mulberry(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  }
  return () => {
    h |= 0;
    h = (h + 0x6d2b79f5) | 0;
    let t = Math.imul(h ^ (h >>> 15), 1 | h);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace<T>(items: T[], next: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    const a = items[i];
    const b = items[j];
    if (a === undefined || b === undefined) continue;
    items[i] = b;
    items[j] = a;
  }
  return items;
}

function orthoNeighbours(position: number): number[] {
  const { col, row } = findFourCell(position);
  const out: number[] = [];
  if (col > 0) out.push(findFourPosition(col - 1, row));
  if (col < FIND_FOUR_SIZE - 1) out.push(findFourPosition(col + 1, row));
  if (row > 0) out.push(findFourPosition(col, row - 1));
  if (row < FIND_FOUR_SIZE - 1) out.push(findFourPosition(col, row + 1));
  return out;
}

function componentSize(start: number, occupied: ReadonlySet<number>): number {
  const seen = new Set<number>([start]);
  const stack = [start];
  while (stack.length > 0) {
    const at = stack.pop();
    if (at === undefined) break;
    for (const n of orthoNeighbours(at)) {
      if (seen.has(n) || !occupied.has(n)) continue;
      seen.add(n);
      stack.push(n);
    }
  }
  return seen.size;
}

/**
 * With a +1 budget a team can only *end* on a non-red cell, so every corner
 * must stay 4-connected to the centre through white cells.
 */
export function findFourCornersOpen(red: ReadonlySet<number>): boolean {
  const seen = new Set<number>([FIND_FOUR_CENTER]);
  const stack = [FIND_FOUR_CENTER];
  while (stack.length > 0) {
    const at = stack.pop();
    if (at === undefined) break;
    for (const n of orthoNeighbours(at)) {
      if (seen.has(n) || red.has(n)) continue;
      seen.add(n);
      stack.push(n);
    }
  }
  return FIND_FOUR_CORNERS.every((corner) => seen.has(corner));
}

export function dealFindFourBoard(seed: string): FindFourBoard {
  const next = mulberry(seed);
  const powerups: FindFourPowerup[] = shuffleInPlace([...POWERUP_IDS], next).map((id, i) => ({
    id,
    position: FIND_FOUR_CORNERS[i] ?? findFourPosition(0, 0),
  }));

  const forbidden = new Set<number>([FIND_FOUR_CENTER, ...FIND_FOUR_CORNERS]);
  const pool = shuffleInPlace(
    Array.from({ length: FIND_FOUR_TILES }, (_, p) => p).filter((p) => !forbidden.has(p)),
    next,
  );

  const red = new Set<number>();
  for (const candidate of pool) {
    if (red.size >= FIND_FOUR_RED_COUNT) break;
    const trial = new Set(red);
    trial.add(candidate);
    if (componentSize(candidate, trial) > 3) continue;
    if (!findFourCornersOpen(trial)) continue;
    red.add(candidate);
  }

  if (red.size < FIND_FOUR_RED_COUNT) {
    // A pathological seed left gaps; fill remaining cells that still obey the cap.
    for (const candidate of pool) {
      if (red.size >= FIND_FOUR_RED_COUNT) break;
      if (red.has(candidate)) continue;
      const trial = new Set(red);
      trial.add(candidate);
      if (componentSize(candidate, trial) > 3) continue;
      if (!findFourCornersOpen(trial)) continue;
      red.add(candidate);
    }
  }

  return { redPositions: [...red].sort((a, b) => a - b), powerups };
}

export function pickRandomCorner(seed: string, avoid: number): number {
  const next = mulberry(seed);
  const options = FIND_FOUR_CORNERS.filter((p) => p !== avoid);
  const pick = options[Math.floor(next() * options.length)];
  return pick ?? FIND_FOUR_CENTER;
}

export function rectGridLayout(size: number): {
  columns: number;
  rows: number;
  cells: { column: number; row: number }[];
} {
  const cells: { column: number; row: number }[] = [];
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      cells.push({ column: col + 1, row: row + 1 });
    }
  }
  return { columns: size, rows: size, cells };
}
